import { LinearProgress } from '@material-ui/core';
import { Color, ColorDynamic, Stack } from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import styled from 'styled-components';
import type { LoadStatus } from '../data/LoadDTO';
import { useLoadPhotoSyncStatus } from '../data/LoadsAPI';
import { getLoadPhotoSyncCountsFromApi } from './loadPhotoSyncStatus';
import { LoadInspectionSyncTooltipSkeleton } from './LoadInspectionSyncTooltipSkeleton';

const StyledLinearProgress = styled(LinearProgress)`
  & {
    height: 3px;
    border-radius: 4px;
  }

  .MuiLinearProgress-barColorPrimary {
    background-color: ${ColorDynamic.Blue50};
    border-radius: 4px;
  }

  &.MuiLinearProgress-colorPrimary {
    background-color: ${Color.Dark300};

    ${({ theme }) =>
      theme.palette.type === 'dark' &&
      `
      background-color: ${Color.Dark100};
    `}
  }
`;

interface LoadInspectionSyncTooltipContentWrapperProps {
  loadGuid: string;
  loadStatus: LoadStatus;
  synced: number;
  total: number;
  shouldFetchFreshCounts: boolean;
  isTooltipOpen: boolean;
}

export function LoadInspectionSyncTooltipContentWrapper({
  loadGuid,
  loadStatus,
  synced,
  total,
  shouldFetchFreshCounts,
  isTooltipOpen,
}: LoadInspectionSyncTooltipContentWrapperProps) {
  const shouldQuery = shouldFetchFreshCounts && isTooltipOpen && !!loadGuid;

  const { data, isLoading } = useLoadPhotoSyncStatus(loadGuid, {
    enabled: shouldQuery,
    staleTime: 5000,
  });

  if (shouldQuery && isLoading && !data) {
    return <LoadInspectionSyncTooltipSkeleton />;
  }

  if (shouldFetchFreshCounts && data) {
    const counts = getLoadPhotoSyncCountsFromApi(data, loadStatus);
    return (
      <LoadInspectionSyncTooltipContent
        synced={counts.synced}
        total={counts.total}
      />
    );
  }

  return <LoadInspectionSyncTooltipContent synced={synced} total={total} />;
}

interface LoadInspectionSyncTooltipContentProps {
  synced: number;
  total: number;
}

export function LoadInspectionSyncTooltipContent({
  synced,
  total,
}: LoadInspectionSyncTooltipContentProps) {
  const progress = total > 0 ? Math.round((synced / total) * 100) : 0;

  return (
    <Box padding="xsmall" minWidth="240px" maxWidth="450px">
      <Stack space="xxsmall">
        <TextBox color="white" variant="body-semibold" noWrap={true}>
          Inspection photos syncing from Driver App...
        </TextBox>
        <StyledLinearProgress
          color="primary"
          variant="determinate"
          value={progress}
        />

        <TextBox variant="hint" color="white" noWrap={true}>
          {`${synced} of ${total} photos synced`}
        </TextBox>
      </Stack>
    </Box>
  );
}
